import { apiClient } from './client';
import { apiBaseURL } from './config';

export async function getSystemHealth(): Promise<{ status: string; timestamp?: string }> {
  const response = await fetch(`${apiBaseURL}/api/v1/health`);
  if (!response.ok) throw new Error('Health check failed');
  return response.json();
}

export interface AiIntegrationSettings {
  provider: 'openai' | 'groq' | 'custom';
  baseUrl: string;
  model: string;
  keyConfigured: boolean;
  keySuffix: string | null;
  updatedAt: string | null;
}

export async function getAiIntegrationSettings(): Promise<AiIntegrationSettings> {
  const response = await apiClient.get('/api/v1/admin/ai-integration');
  return response.data.data;
}

export async function saveAiIntegrationSettings(payload: { provider: AiIntegrationSettings['provider']; baseUrl: string; model: string; apiKey?: string }): Promise<AiIntegrationSettings> {
  const response = await apiClient.put('/api/v1/admin/ai-integration', payload);
  return response.data.data;
}

export async function testAiIntegration(): Promise<void> {
  await apiClient.post('/api/v1/admin/ai-integration/test');
}

export interface DashboardOverview {
  activeInstallations: number;
  activeInstallationsByVersion: Array<{ version: string; count: number }>;
  sessions: number;
  tipsShown: number;
  helpfulRate: number;
  tutorialRuns: number;
  tutorialCompletionRate: number;
  tutorialStepCompletionRate: number;
  hotspotInteractionRate: number;
  topApps: Array<{ appName: string; count: number }>;
  topFailureReasons: Array<{ reason: string; count: number }>;
  dataFreshness: string;
}

export interface Installation {
  id: string;
  clientId: string;
  status: string;
  clientVersion: string;
  platform: string;
  lastSeenAt: string;
  createdAt: string;
}

export interface TutorialAnalytic {
  tutorialKey: string;
  appName: string;
  starts: number;
  completions: number;
  failures: number;
  averageRating: number | null;
  stepCompletionRates: Record<number, number>;
  stepFailureRates: Record<number, number>;
  hotspotInteractionRate: number;
}

// Dashboard APIs
export async function getDashboardOverview(from?: string, to?: string): Promise<DashboardOverview> {
  const response = await apiClient.get('/api/v1/admin/analytics/overview', {
    params: { from, to },
  });
  return response.data.data;
}

// Installation APIs
export async function listInstallations(
  limit = 50,
  offset = 0,
  filters?: Record<string, string | number | undefined>
): Promise<{ data: Installation[]; pagination: any }> {
  const response = await apiClient.get('/api/v1/admin/installations', {
    params: { limit, offset, ...filters },
  });
  return response.data;
}

export async function getInstallationDetail(installationId: string): Promise<any> {
  const response = await apiClient.get(`/api/v1/admin/installations/${installationId}`);
  return response.data.data;
}

export interface InstallationDetail extends Installation {
  architecture?: string;
  locale?: string;
  capabilities: Record<string, unknown>;
  telemetryEnabled: boolean;
  session_count: string;
  tips_shown: string;
  tutorial_runs: string;
  recentTutorials: Array<{
    id: string;
    tutorialKey: string;
    appName: string;
    title: string;
    status: string;
    startedAt: string;
    completedAt: string | null;
    failedAt: string | null;
    failureReasonCode: string | null;
  }>;
}

// Tutorial Analytics
export async function getTutorialAnalytics(filters?: Record<string, any>): Promise<any> {
  const response = await apiClient.get('/api/v1/admin/analytics/tutorials', {
    params: filters,
  });
  return response.data;
}

// Hotspot Analytics
export async function getHotspotAnalytics(filters?: Record<string, any>): Promise<any> {
  const response = await apiClient.get('/api/v1/admin/analytics/hotspots', {
    params: filters,
  });
  return response.data;
}

export interface AuditLog {
  id: string;
  adminUserId: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export async function listAuditLogs(filters: Record<string, string | number | undefined> = {}): Promise<{ logs: AuditLog[]; pagination: any }> {
  const response = await apiClient.get('/api/v1/admin/logs', { params: filters });
  return { logs: response.data.data, pagination: response.data.pagination };
}

export interface TutorialRun {
  id: string;
  tutorialKey: string;
  appName: string;
  title: string;
  status: 'in_progress' | 'completed' | 'failed';
  startedAt: string;
  completedAt: string | null;
  failedAt: string | null;
  failureReasonCode: string | null;
  installation: {
    id: string;
    clientId: string;
    clientVersion: string | null;
    platform: string | null;
    architecture: string | null;
    locale: string | null;
  };
  rating: number | null;
  steps: Array<{
    id: string;
    stepIndex: number;
    completed: boolean;
    completionMethod: string | null;
    failureReasonCode: string | null;
    stepText: string | null;
    clickedHotspot: unknown;
    shownHotspots: unknown;
    rankerVersion: string | null;
    completedAt: string | null;
  }>;
}

export async function listTutorialRuns(filters: Record<string, string | number | undefined> = {}): Promise<{ runs: TutorialRun[]; pagination: any }> {
  const response = await apiClient.get('/api/v1/admin/analytics/tutorial-runs', { params: filters });
  return { runs: response.data.data, pagination: response.data.pagination };
}

export async function getTutorialRunDetail(tutorialRunId: string): Promise<TutorialRun> {
  const response = await apiClient.get(`/api/v1/admin/tutorials/${tutorialRunId}`);
  return response.data.data;
}

export interface LearningCourse {
  id: number;
  title: string;
  shortdesc?: string | null;
  description?: string | null;
  platform?: string | null;
  category_title?: string | null;
  lesson_count?: number | null;
}

export interface LearningLesson {
  id: number;
  title: string;
  video?: string | null;
  videotype?: string | null;
  course_id?: number | null;
  course_title?: string | null;
  pdf_url?: string | null;
  doc_url?: string | null;
  has_subtitles: boolean;
}

export async function listLearningCourses(search?: string): Promise<{ courses: LearningCourse[]; count: number }> {
  const response = await apiClient.get('/api/v1/admin/learning/courses', { params: { search } });
  return response.data.data;
}

export async function listLearningLessons(courseId?: number): Promise<{ lessons: LearningLesson[]; count: number }> {
  const response = await apiClient.get('/api/v1/admin/learning/lessons', { params: { courseId } });
  return response.data.data;
}

export interface TutorialDefinition {
  id: string;
  tutorialKey: string;
  appName: string;
  title: string;
  sourcePdfUrl: string | null;
  steps: Array<{ stepIndex: number; instruction: string; target?: Record<string, unknown>; sourcePage?: number; startTime?: number; endTime?: number; images?: Array<{ pageNumber: number; name: string; dataUrl: string }> }>;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
}

export async function listTutorialDefinitions(): Promise<TutorialDefinition[]> {
  const response = await apiClient.get('/api/v1/admin/tutorial-definitions');
  return response.data.data;
}

export async function createTutorialDefinition(payload: {
  tutorialKey: string;
  appName: string;
  title: string;
  sourcePdfUrl?: string;
  steps: TutorialDefinition['steps'];
}): Promise<TutorialDefinition> {
  const response = await apiClient.post('/api/v1/admin/tutorial-definitions', payload);
  return response.data.data;
}

export type TutorialStepSource = 'pdf' | 'subtitles';

export async function generateTutorialSteps(payload: {
  source: TutorialStepSource;
  lessonId: number;
  tutorialKey: string;
  appName: string;
  title: string;
}): Promise<TutorialDefinition> {
  const response = await apiClient.post('/api/v1/admin/tutorial-definitions/generate', payload);
  return response.data.data;
}

export async function updateTutorialDefinitionStatus(id: string, status: TutorialDefinition['status']): Promise<TutorialDefinition> {
  const response = await apiClient.patch(`/api/v1/admin/tutorial-definitions/${id}/status`, { status });
  return response.data.data;
}

export async function updateTutorialDefinitionContent(id: string, payload: {
  tutorialKey: string;
  appName: string;
  title: string;
  steps: TutorialDefinition['steps'];
}): Promise<TutorialDefinition> {
  const response = await apiClient.patch(`/api/v1/admin/tutorial-definitions/${id}`, payload);
  return response.data.data;
}

export async function deleteTutorialDefinition(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/tutorial-definitions/${id}`);
}

export interface ApiUser {
  id: string;
  clientId: string;
  status: 'active' | 'disabled';
  clientVersion: string;
  platform: string;
  architecture?: string;
  locale?: string;
  telemetryEnabled: boolean;
  lastSeenAt?: string;
  createdAt: string;
}

export async function listApiUsers(): Promise<{ users: ApiUser[]; limit: number; offset: number; hasMore: boolean }> {
  const response = await apiClient.get('/api/v1/admin/api-users');
  return response.data.data;
}

export async function createApiUser(payload: Record<string, unknown>): Promise<{ user: ApiUser; clientSecret: string }> {
  const response = await apiClient.post('/api/v1/admin/api-users', payload);
  return response.data.data;
}

export async function updateApiUser(userId: string, status: 'active' | 'disabled'): Promise<ApiUser> {
  const response = await apiClient.patch(`/api/v1/admin/api-users/${userId}`, { status });
  return response.data.data;
}

export async function deleteApiUser(userId: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/api-users/${userId}`);
}
